import { Button, Group, Modal, ScrollArea, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import { useEffect, useState } from 'react';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { primaryColor } from '../../constants/theme';
import { useApp } from '../../context/AppContext';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = Array.from({ length: 12 }, (_, i) => addMonths(startOfMonth(new Date()), i));

const buildDays = (month: Date): Date[] => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days: Date[] = [];
  let cursor = gridStart;
  while (cursor <= gridEnd) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return days;
};

const MonthButton = ({
  month,
  isSelected,
  onClick,
}: {
  month: Date;
  isSelected: boolean;
  onClick: () => void;
}) => (
  <UnstyledButton
    onClick={onClick}
    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}
  >
    <div style={{
      border: `1px solid ${isSelected ? primaryColor : '#ced4da'}`,
      borderRadius: '50%',
      width: 52,
      height: 52,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: isSelected ? primaryColor : 'transparent',
    }}>
      <Text size="xs" fw={600} c={isSelected ? 'white' : primaryColor} lh={1}>
        {format(month, 'MMM')}
      </Text>
    </div>
    <Text size="xs" c="dimmed" lh={1}>{format(month, 'yyyy')}</Text>
  </UnstyledButton>
);

interface MultiDatePickerModalProps {
  opened: boolean;
  label: string;
  initialDates: string[];
  onClose: () => void;
  onConfirm: (dates: string[]) => void;
}

export const MultiDatePickerModal = ({
  opened,
  label,
  initialDates,
  onClose,
  onConfirm,
}: MultiDatePickerModalProps) => {
  const today = startOfDay(new Date());
  const [displayedMonth, setDisplayedMonth] = useState(startOfMonth(today));
  const [selectedDates, setSelectedDates] = useState<Set<string>>(
    () => new Set(initialDates)
  );

  useEffect(() => {
    if (opened) {
      setDisplayedMonth(startOfMonth(today));
      setSelectedDates(new Set(initialDates));
    }
  }, [opened]);
  const { isTabletAndBelow } = useApp();

  const days = buildDays(displayedMonth);

  const toggleDate = (isoDate: string) => {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(isoDate)) {
        next.delete(isoDate);
      } else {
        next.add(isoDate);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(selectedDates).sort());
  };

  const selectedCount = selectedDates.size;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      padding="lg"
      title={
        <Text fw={600} size="h4">
          {label}
          {selectedCount > 0 && (
            <Text component="span" c={primaryColor} fw={600} size="h4">
              {' '}({selectedCount})
            </Text>
          )}
        </Text>
      }
    >
      <Stack gap="md">
        {/* Month picker */}
        {isTabletAndBelow ? (
          <ScrollArea type="auto" offsetScrollbars>
            <Group wrap="nowrap" gap={8} pb={4}>
              {MONTHS.map((month) => (
                <MonthButton
                  key={month.toISOString()}
                  month={month}
                  isSelected={isSameMonth(month, displayedMonth)}
                  onClick={() => setDisplayedMonth(month)}
                />
              ))}
            </Group>
          </ScrollArea>
        ) : (
          <SimpleGrid cols={6} spacing={6}>
            {MONTHS.map((month) => (
              <MonthButton
                key={month.toISOString()}
                month={month}
                isSelected={isSameMonth(month, displayedMonth)}
                onClick={() => setDisplayedMonth(month)}
              />
            ))}
          </SimpleGrid>
        )}

        {/* Calendar grid */}
        <SimpleGrid cols={7} spacing={4} mt="md">
          {DAY_LABELS.map((day) => (
            <Text key={day} ta="center" size="xs" c="dimmed" fw={500}>
              {day}
            </Text>
          ))}

          {days.map((day) => {
            const isoDate = format(day, 'yyyy-MM-dd');
            const inMonth = isSameMonth(day, displayedMonth);
            const isPast = isBefore(startOfDay(day), today);
            const isDisabled = isPast || !inMonth;
            const isSelected = selectedDates.has(isoDate);

            return (
              <UnstyledButton
                key={isoDate}
                disabled={isDisabled}
                onClick={() => !isDisabled && toggleDate(isoDate)}
                style={(theme) => ({
                  border: inMonth
                    ? `1px solid ${isSelected ? theme.colors[primaryColor]?.[6] ?? primaryColor : theme.colors.gray[3]}`
                    : 'none',
                  borderRadius: theme.radius.sm,
                  aspectRatio: '1',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isSelected ? (theme.colors[primaryColor]?.[6] ?? primaryColor) : 'transparent',
                  opacity: isDisabled ? 0.3 : 1,
                  cursor: isDisabled ? 'default' : 'pointer',
                  minHeight: 36,
                })}
              >
                <Text
                  size="xs"
                  fw={isSelected ? 700 : 400}
                  c={isSelected ? 'white' : inMonth ? 'dark' : 'dimmed'}
                >
                  {inMonth ? format(day, 'd') : ''}
                </Text>
              </UnstyledButton>
            );
          })}
        </SimpleGrid>

        <Button color={primaryColor} fullWidth onClick={handleConfirm}>
          Apply{selectedCount > 0 ? ` (${selectedCount})` : ''}
        </Button>
      </Stack>
    </Modal>
  );
};
